import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Truck, Loader2, X } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { fetchPickupCouriers } from '../../../../features/pickupRequests/pickupRequestsThunks';
import {
  clearPickupError,
  resetPickupRequests,
} from '../../../../features/pickupRequests/pickupRequestsSlice';
import { CreatePickupRequestModal } from './CreatePickupRequestModal';

/**
 * The admin's copy of the seller's screen: the couriers that need pickup
 * requests (only Samex today) across the admin's sellers, each opening the form
 * that schedules one.
 */
export const CreatePickupRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { availableCouriers, couriersLoaded, error } = useSelector((state) => state.pickupRequests);

  const [formCourier, setFormCourier] = useState(null);

  useEffect(() => {
    dispatch(fetchPickupCouriers());
    return () => {
      dispatch(resetPickupRequests());
    };
  }, [dispatch]);

  return (
    <div data-testid="pickup-create-page" className="space-y-6">
      <div className="flex justify-end">
        <Button variant="ghost" onClick={() => navigate('/pickup-requests/assign')}>
          Ver solicitudes y asignar pedidos
        </Button>
      </div>

      {error && (
        <div
          data-testid="pickup-error"
          className="flex items-center justify-between gap-4 bg-red-50 dark:bg-red-900/20 text-danger px-5 py-3 rounded-2xl border border-red-200 dark:border-red-900 text-sm font-medium"
        >
          <span>{error}</span>
          <button type="button" aria-label="Cerrar error" onClick={() => dispatch(clearPickupError())}>
            <X size={16} />
          </button>
        </div>
      )}

      {!couriersLoaded && (
        <div className="flex justify-center py-8">
          <Loader2 size={28} className="text-ninja-green animate-spin" />
        </div>
      )}

      {/* The legacy's "Contacta a tu operador" is the seller's way out; the
          admin is the one who turns pickups on, from the seller's detail. */}
      {couriersLoaded && !error && availableCouriers.length === 0 && (
        <div
          data-testid="pickup-empty-couriers"
          className="bg-blue-50 dark:bg-blue-900/20 text-info px-5 py-3 rounded-2xl border border-blue-200 dark:border-blue-900 text-sm"
        >
          Ninguno de tus sellers tiene couriers con solicitudes de retiro activas. Actívalas en el
          detalle del seller (pestaña Couriers).
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {availableCouriers.map(({ courier }) => (
          <div
            key={courier}
            data-testid="pickup-courier-card"
            data-courier={courier}
            className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border flex flex-col"
          >
            <div className="flex items-center gap-3 mb-2">
              <Truck size={22} className="text-ninja-green" />
              <h3 className="text-lg font-bold text-ninja-dark dark:text-white capitalize">{courier}</h3>
            </div>
            <p className="text-sm text-text-secondary flex-1">
              Agenda un retiro de {courier.toUpperCase()} para hoy o un día futuro, desde una bodega de
              tus sellers.
            </p>
            <Button variant="primary" fullWidth className="mt-5" onClick={() => setFormCourier(courier)}>
              Crear solicitud
            </Button>
          </div>
        ))}
      </div>

      <CreatePickupRequestModal
        isOpen={Boolean(formCourier)}
        onClose={() => setFormCourier(null)}
        courier={formCourier}
      />
    </div>
  );
};
